// src/pages/CustomReportsPage.tsx
// Custom Reports — SQL query runner with saved queries and raw SQL mode
// Access controlled by customReports feature flag + GeniReports Admin role for raw SQL

import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Play, Database, ChevronDown, Clock, AlertCircle,
  Download, Copy, Check, Terminal, FileText, Search,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { apiV2 } from '../services/apiV2';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

interface SavedQueryParam {
  name: string;
  label: string;
  type: 'text' | 'date' | 'number';
  default?: string;
  required?: boolean;
}

interface SavedQueryDef {
  id: string;
  name: string;
  description: string;
  category: string;
  params: SavedQueryParam[];
}

interface QueryResult {
  rows: Record<string, unknown>[];
  fields: string[];
  rowCount: number;
  elapsed: number;
  sql: string;
}

interface CustomReportsPageProps {
  onBack?: () => void;
}

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

const formatValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    if (Number.isInteger(val)) return val.toLocaleString();
    return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  const s = String(val).trim();
  // Date detection
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s).toLocaleDateString();
  return s;
};

const downloadCsv = (result: QueryResult) => {
  const header = result.fields.join(',');
  const rows = result.rows.map(row =>
    result.fields.map(f => {
      const v = String(row[f] ?? '');
      return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `query-results-${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

export default function CustomReportsPage({ onBack }: CustomReportsPageProps) {
  const { hasRole } = useAuthContext();
  const canRunRawSql = hasRole('GeniReports Admin');

  // State
  const [mode, setMode] = useState<'saved' | 'raw'>('saved');
  const [selectedQueryId, setSelectedQueryId] = useState<string>('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [rawSql, setRawSql] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch saved queries list
  const { data: savedQueries } = useQuery<SavedQueryDef[]>({
    queryKey: ['savedQueries'],
    queryFn: async () => {
      const res = await apiV2['client'].get('/.netlify/functions/run-query');
      return res.data?.data?.queries || [];
    },
    retry: 1,
    staleTime: 1000 * 60 * 30,
  });

  const selectedQuery = useMemo(
    () => savedQueries?.find(q => q.id === selectedQueryId) || null,
    [savedQueries, selectedQueryId]
  );

  const categories = useMemo(() => {
    if (!savedQueries) return [];
    return [...new Set(savedQueries.map(q => q.category))].sort();
  }, [savedQueries]);

  const filteredQueries = useMemo(() => {
    if (!savedQueries) return [];
    if (!searchTerm) return savedQueries;
    const s = searchTerm.toLowerCase();
    return savedQueries.filter(q =>
      q.name.toLowerCase().includes(s) || q.description.toLowerCase().includes(s) || q.category.toLowerCase().includes(s)
    );
  }, [savedQueries, searchTerm]);

  // Select a saved query and set defaults
  const selectQuery = useCallback((id: string) => {
    setSelectedQueryId(id);
    setResult(null);
    setError(null);
    const q = savedQueries?.find(sq => sq.id === id);
    if (q) {
      const defaults: Record<string, string> = {};
      q.params.forEach(p => { if (p.default) defaults[p.name] = p.default; });
      setParamValues(defaults);
    }
  }, [savedQueries]);

  // Execute query
  const runQuery = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const body: Record<string, unknown> = {};
      if (mode === 'saved' && selectedQueryId) {
        body.queryId = selectedQueryId;
        body.params = paramValues;
      } else if (mode === 'raw' && rawSql.trim()) {
        body.sql = rawSql.trim();
      } else {
        setError(mode === 'saved' ? 'Select a query first' : 'Enter a SQL query');
        setLoading(false);
        return;
      }

      const res = await apiV2['client'].post('/.netlify/functions/run-query', body);
      if (res.data?.success) {
        setResult(res.data.data);
      } else {
        setError(res.data?.error || 'Query failed');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (err as { message?: string })?.message || 'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyResults = () => {
    if (!result) return;
    const text = result.fields.join('\t') + '\n' +
      result.rows.map(row => result.fields.map(f => String(row[f] ?? '')).join('\t')).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ═════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all">
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Database className="text-emerald-400" size={28} />
                Custom Reports
              </h1>
              <p className="text-slate-400 mt-1 ml-10">Run saved queries or custom SQL against your database</p>
            </div>
          </div>

          {/* Mode toggle */}
          {canRunRawSql && (
            <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-lg p-1">
              <button
                onClick={() => { setMode('saved'); setResult(null); setError(null); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'saved' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <FileText size={14} className="inline mr-2" />Saved Queries
              </button>
              <button
                onClick={() => { setMode('raw'); setResult(null); setError(null); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'raw' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <Terminal size={14} className="inline mr-2" />Raw SQL
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left panel — Query selection / editor */}
          <div className="lg:col-span-1">
            {mode === 'saved' ? (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-700/50">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Search queries..."
                      className="w-full pl-9 pr-4 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    />
                  </div>
                </div>

                <div className="max-h-[500px] overflow-y-auto">
                  {categories.map(cat => {
                    const catQueries = filteredQueries.filter(q => q.category === cat);
                    if (catQueries.length === 0) return null;
                    return (
                      <div key={cat}>
                        <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/30">
                          {cat}
                        </div>
                        {catQueries.map(q => (
                          <button
                            key={q.id}
                            onClick={() => selectQuery(q.id)}
                            className={`w-full text-left px-4 py-3 border-b border-slate-700/30 transition-all ${
                              selectedQueryId === q.id ? 'bg-emerald-500/10 border-l-2 border-l-emerald-500' : 'hover:bg-slate-700/30'
                            }`}
                          >
                            <div className="text-sm font-medium text-white">{q.name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{q.description}</div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">SQL Query</label>
                <textarea
                  value={rawSql}
                  onChange={e => setRawSql(e.target.value)}
                  placeholder="SELECT * FROM Customer WHERE Code = 9188"
                  rows={12}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-sm text-emerald-300 font-mono placeholder-slate-600 focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-y"
                  spellCheck={false}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); runQuery(); } }}
                />
                <p className="text-xs text-slate-500 mt-2">Ctrl+Enter to run. Read-only queries only (SELECT).</p>
              </div>
            )}
          </div>

          {/* Right panel — Parameters + Results */}
          <div className="lg:col-span-2 space-y-6">
            {/* Parameters (saved mode) */}
            {mode === 'saved' && selectedQuery && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <ChevronDown size={16} className="text-emerald-400" />
                  {selectedQuery.name}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedQuery.params.map(param => (
                    <div key={param.name}>
                      <label className="block text-xs text-slate-400 mb-1.5">
                        {param.label} {param.required && <span className="text-red-400">*</span>}
                      </label>
                      <input
                        type={param.type === 'date' ? 'date' : param.type === 'number' ? 'number' : 'text'}
                        value={paramValues[param.name] || ''}
                        onChange={e => setParamValues(prev => ({ ...prev, [param.name]: e.target.value }))}
                        placeholder={param.default || ''}
                        className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-white focus:ring-2 focus:ring-emerald-500/50"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Run button */}
            <button
              onClick={runQuery}
              disabled={loading || (mode === 'saved' && !selectedQueryId) || (mode === 'raw' && !rawSql.trim())}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Running query...
                </>
              ) : (
                <>
                  <Play size={18} />
                  Run Query
                </>
              )}
            </button>

            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-400 font-medium">Query Error</p>
                  <p className="text-xs text-red-300/80 mt-1 font-mono">{error}</p>
                </div>
              </div>
            )}

            {/* Results */}
            {result && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                {/* Results header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-white font-medium">{result.rowCount} rows</span>
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock size={12} /> {result.elapsed}ms
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={copyResults}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white bg-slate-700/50 rounded-lg transition-colors"
                    >
                      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      onClick={() => downloadCsv(result)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white bg-slate-700/50 rounded-lg transition-colors"
                    >
                      <Download size={12} /> CSV
                    </button>
                  </div>
                </div>

                {/* Executed SQL */}
                <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-700/30">
                  <pre className="text-xs text-slate-500 font-mono overflow-x-auto whitespace-pre-wrap">{result.sql}</pre>
                </div>

                {/* Data table */}
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-slate-500 font-medium w-10">#</th>
                        {result.fields.map(f => (
                          <th key={f} className="px-3 py-2 text-left text-xs text-slate-400 font-semibold uppercase tracking-wider whitespace-nowrap">{f}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                      {result.rows.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-700/20 transition-colors">
                          <td className="px-3 py-2 text-xs text-slate-600">{i + 1}</td>
                          {result.fields.map(f => (
                            <td key={f} className="px-3 py-2 text-slate-300 whitespace-nowrap">{formatValue(row[f])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {result.rowCount === 0 && (
                  <div className="px-4 py-8 text-center text-slate-500 text-sm">No rows returned</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
